import { api } from "../common/utils"
import {
  PaymentSettings,
  AddPaymentMethodRequest,
  SetDefaultPaymentMethodRequest,
  RemovePaymentMethodRequest,
  UpdateBillingAddressRequest,
  UpdateBillingEmailRequest,
  ApiResponse
} from "../types/payment"

const PAYMENT_SETTINGS_ENDPOINT = "/api/v1/payment-settings"
const PAYMENT_METHOD_ENDPOINT = "/api/v1/payment-method"
const PAYMENT_METHOD_DEFAULT_ENDPOINT = "/api/v1/payment-method-default"
const BILLING_ADDRESS_ENDPOINT = "/api/v1/billing-address"
const BILLING_EMAIL_ENDPOINT = "/api/v1/billing-email"
const PAYMENT_PORTAL_ENDPOINT = "/api/v1/payment-portal-session"

/**
 * Fetch payment settings for an organization
 */
const fetchPaymentSettings = async (
  orgId: string
): Promise<PaymentSettings> => {
  const response = await api.get<any>(
    `${PAYMENT_SETTINGS_ENDPOINT}?org_id=${orgId}`
  )

  if (response.data.status === "error") {
    throw new Error(response.data.message)
  }

  // Backend returns data directly (not nested in data property)
  return response.data
}

/**
 * Add a new payment method
 */
const addPaymentMethod = async (
  request: AddPaymentMethodRequest
): Promise<void> => {
  const response = await api.post<ApiResponse>(PAYMENT_METHOD_ENDPOINT, request)

  if (response.data.status === "error") {
    throw new Error(response.data.message)
  }
}

/**
 * Remove a payment method
 */
const removePaymentMethod = async (
  request: RemovePaymentMethodRequest
): Promise<void> => {
  const response = await api.delete<ApiResponse>(
    `${PAYMENT_METHOD_ENDPOINT}?org_id=${request.org_id}&payment_method_id=${request.payment_method_id}`
  )

  if (response.data.status === "error") {
    throw new Error(response.data.message)
  }
}

/**
 * Set default payment method
 */
const setDefaultPaymentMethod = async (
  request: SetDefaultPaymentMethodRequest
): Promise<void> => {
  const response = await api.post<ApiResponse>(
    PAYMENT_METHOD_DEFAULT_ENDPOINT,
    request
  )

  if (response.data.status === "error") {
    throw new Error(response.data.message)
  }
}

/**
 * Update billing address
 */
const updateBillingAddress = async (
  request: UpdateBillingAddressRequest
): Promise<void> => {
  const response = await api.post<ApiResponse>(
    BILLING_ADDRESS_ENDPOINT,
    request
  )

  if (response.data.status === "error") {
    throw new Error(response.data.message)
  }
}

/**
 * Update billing email
 */
const updateBillingEmail = async (
  request: UpdateBillingEmailRequest
): Promise<void> => {
  const response = await api.post<ApiResponse>(BILLING_EMAIL_ENDPOINT, request)

  if (response.data.status === "error") {
    throw new Error(response.data.message)
  }
}

/**
 * Get Stripe Billing Portal URL (fallback)
 */
const getPaymentPortalUrl = async (orgId: string): Promise<string> => {
  const response = await api.get<any>(
    `${PAYMENT_PORTAL_ENDPOINT}?org_id=${orgId}`
  )

  if (response.data.status === "error") {
    throw new Error(response.data.message)
  }

  // Backend returns data directly
  return response.data.url
}

const payment = {
  fetchPaymentSettings,
  addPaymentMethod,
  removePaymentMethod,
  setDefaultPaymentMethod,
  updateBillingAddress,
  updateBillingEmail,
  getPaymentPortalUrl
}

export default payment
